import { all, call, put, takeEvery, select } from 'redux-saga/effects';
import { defineKqlQuery, searchSubmissions } from '@kineticdata/react';
import { selectServicesKapp, C } from '@kineticdata/bundle-common';
import { actions, types, searchFields, typeStates } from '../modules/approvals';
import { Map } from 'immutable';

const buildSearch = (search, profile) => {
  const searcher = defineKqlQuery();
  // Add type and coreState
  searcher.equals('type', 'type').equals('coreState', 'coreState');
  // Check if filter query is provided
  let searcherFilterValues = {};
  const isFilterQuery = Array.isArray(search.query) && search.query.length > 0;
  // Is assigned team filtered
  const assignedTeamFilter =
    isFilterQuery &&
    search.query.find(
      ({ field, value }) => field.value === `values[${C.FIELD_ASSIGNED_TEAMS}]`,
    );
  // Is assigned individual filtered
  const assignedIndividualFilter =
    isFilterQuery &&
    search.query.find(
      ({ field, value }) =>
        field.value === `values[${C.FIELD_ASSIGNED_INDIVIDUALS}]`,
    );
  // Add assignment parts
  if (assignedTeamFilter) {
    searcher.equals(
      assignedTeamFilter.field.value,
      assignedTeamFilter.field.id,
    );
    searcherFilterValues[assignedTeamFilter.field.id] =
      assignedTeamFilter.value;
  } else if (assignedIndividualFilter) {
    searcher.equals(
      assignedIndividualFilter.field.value,
      assignedIndividualFilter.field.id,
    );
    searcherFilterValues[assignedIndividualFilter.field.id] =
      assignedIndividualFilter.value;
  } else {
    // Add standard assignment parts if neither was specified
    if (profile.memberships.length > 0) {
      searcher
        .or()
        .in(`values[${C.FIELD_ASSIGNED_TEAMS}]`, 'teams')
        .equals(`values[${C.FIELD_ASSIGNED_INDIVIDUALS}]`, 'username')
        .end();
      searcherFilterValues['teams'] = profile.memberships.map(m => m.team.name);
      searcherFilterValues['username'] = profile.username;
    } else {
      searcher.equals(`values[${C.FIELD_ASSIGNED_INDIVIDUALS}]`, 'username');
      searcherFilterValues['username'] = profile.username;
    }
  }
  // Add non-assignment filter parts
  if (Array.isArray(search.query)) {
    search.query
      .filter(
        ({ field }) =>
          ![
            `values[${C.FIELD_ASSIGNED_INDIVIDUALS}]`,
            `values[${C.FIELD_ASSIGNED_TEAMS}]`,
          ].includes(field.value),
      )
      .forEach(({ field, value }) => {
        if (!field) {
          searcher.or();
          for (let i = 0; i < searchFields.length; i++) {
            searcher.equals(searchFields[i].value, '_all');
          }
          searcher.end();
          searcherFilterValues['_all'] = value;
        } else {
          searcher.equals(field.value, field.id);
          searcherFilterValues[field.id] = value;
        }
      });
  }

  return {
    q: searcher.end()({
      type: C.APPROVAL_FORM_TYPE,
      coreState: search.coreState,
      ...searcherFilterValues,
    }),
    include: [
      'details',
      'values',
      'form',
      'form.attributes',
      'form.fields',
      'form.kapp',
      'form.kapp.attributes',
      'parent',
      'parent.descendants',
    ],
  };
};

export function* fetchApprovalsRequestSaga(action) {
  const kapp = yield select(selectServicesKapp);
  const profile = yield select(state => state.app.profile);
  const search = yield select(state => state.approvals.search);
  const limit = yield select(state => state.approvals.limit);
  const direction = yield select(state => state.approvals.direction);
  const pageToken = yield select(state => state.approvals.pageToken);
  const {
    submissions,
    error,
    nextPageToken,
    count,
    countPageToken,
  } = yield call(searchSubmissions, {
    kapp: (kapp && kapp.slug) || 'services',
    limit: action.payload && action.payload.homepage ? 5 : limit || 10,
    pageToken,
    orderBy:
      search.coreState === C.CORE_STATE_SUBMITTED ? 'submittedAt' : 'createdAt',
    direction: action.payload && action.payload.homepage ? 'DESC' : direction,
    search: buildSearch(search, profile),
    count: true,
  });

  if (error) {
    yield put(actions.fetchApprovalsFailure({ error }));
  } else {
    yield put(
      actions.fetchApprovalsSuccess({
        data: submissions,
        nextPageToken,
        count,
        countPageToken,
      }),
    );
    // If fetching the first page, update the count
    if (!pageToken) {
      const type = typeStates.findKey(
        (coreState, type) =>
          coreState === search.coreState &&
          type.includes('Other') === !!search.all,
      );
      yield put(actions.countApprovalsComplete({ [type]: count }));

      // If fetching pending approvals with no filters, update home approvals count
      // which is shown in the sidebar
      if (
        search.coreState === C.CORE_STATE_DRAFT &&
        (!search.query || search.query.length === 0)
      ) {
        const setHomeApprovalsCount = yield select(
          state => state.app.actions.setHomeApprovalsCount,
        );
        if (typeof setHomeApprovalsCount === 'function') {
          setHomeApprovalsCount(count);
        }
      }
    }
  }
}

export function* countApprovalsRequestSaga(action) {
  const profile = yield select(state => state.app.profile);
  const kapp = yield select(selectServicesKapp);
  const types = Array.isArray(action.payload)
    ? action.payload
    : !!action.payload
      ? [action.payload]
      : typeStates.keySeq().toJS();
  const counts = yield all(
    types
      .reduce(
        (map, type) =>
          map.set(
            type,
            call(searchSubmissions, {
              kapp: (kapp && kapp.slug) || 'services',
              limit: 0,
              search: buildSearch(
                {
                  coreState: typeStates.get(type),
                  all: type.includes('Other'),
                },
                profile,
              ),
              count: true,
            }),
          ),
        Map(),
      )
      .toJS(),
  );

  yield put(
    actions.countApprovalsComplete(
      Map(counts)
        .map(
          countResults =>
            typeof countResults.count === 'number' ? countResults.count : null,
        )
        .toJS(),
    ),
  );
}

export function* watchApprovals() {
  yield takeEvery(
    [
      types.FETCH_APPROVALS_REQUEST,
      types.FETCH_APPROVALS_CURRENT,
      types.FETCH_APPROVALS_PREVIOUS,
      types.FETCH_APPROVALS_NEXT,
    ],
    fetchApprovalsRequestSaga,
  );
  yield takeEvery(types.COUNT_APPROVALS_REQUEST, countApprovalsRequestSaga);
}
